import { getLocationFromProvider } from "./addressUtils";
import { ensureVendorGxPProfile } from "./myVendorGxpUtils";

function extractServiceCategories(provider = {}) {
  return (provider.provider_capabilities || [])
    .map((cap) => cap.rfi_name || cap.name)
    .filter(Boolean);
}

/**
 * Resolve primary location for cards/tables.
 * Prefer location_type "primary", then is_primary, then first location,
 * then top-level city/state/country on the provider/item.
 */
export function resolvePrimaryLocationDisplay(item = {}, locations = []) {
  const locs = Array.isArray(locations) ? locations : [];
  const primary =
    locs.find((loc) => String(loc?.location_type || "").toLowerCase() === "primary") ||
    locs.find((loc) => loc?.is_primary === true || loc?.isPrimary === true) ||
    locs[0] ||
    null;

  const fromLocation = primary
    ? getLocationFromProvider(primary, { short: true, abbvState: true })
    : "";
  if (fromLocation) return fromLocation;

  const fromTopLevel = getLocationFromProvider(
    {
      city: item.city,
      state: item.state,
      country: item.country,
      address1: item.address1,
    },
    { short: true, abbvState: true }
  );
  return fromTopLevel || null;
}

function formatQualifiedDate(raw) {
  if (!raw) return null;
  try {
    const match = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    }
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return null;
  }
}

function mapRiskScore(riskScore) {
  if (!riskScore || typeof riskScore.score !== "number") return null;
  return {
    score: riskScore.score,
    rating: riskScore.rating || "Green",
  };
}

function mapRequestItem(req) {
  return {
    id: String(req.id || ""),
    type: req.type || "",
    title: req.title || "",
    date: req.date || null,
    location: req.location || null,
    gxps: req.gxps || [],
    services: req.services || [],
    isCompleted: Boolean(req.is_completed),
    status: req.status || null,
    hcQuestionnaireId: req.hc_questionnaire_id ?? null,
    vqaRequestId: req.vqa_request_id ? String(req.vqa_request_id) : null,
    riskScore: mapRiskScore(req.risk_score),
  };
}

function sortRequestItems(items = []) {
  return [...items].sort((a, b) => {
    if (a.isCompleted !== b.isCompleted) return a.isCompleted ? 1 : -1;
    const ad = a.date ? new Date(a.date).getTime() : 0;
    const bd = b.date ? new Date(b.date).getTime() : 0;
    return bd - ad;
  });
}

/**
 * Maps a My Vendors API item into the card view model.
 */
export function mapMyVendorApiItemToVendor(item, { tags = [] } = {}) {
  const id = String(item.id || "");
  const provider = {
    id,
    name: item.name,
    logo: item.logo,
    city: item.city,
    state: item.state,
    country: item.country,
    address1: item.address1,
    provider_type: item.provider_type,
    locations: item.locations || [],
    provider_capabilities: item.provider_capabilities || [],
    rfi_count: item.rfi_count || 0,
    assessment_count: item.assessment_count || 0,
    discovery_status: item.discovery_status,
    created_at: item.created_at,
    is_active: item.is_active,
    users_count: item.users_count,
  };

  const primaryLocation = resolvePrimaryLocationDisplay(item, provider.locations);

  const serviceCategories = extractServiceCategories(provider);
  const hasPlatformActivity = Boolean(item.has_platform_activity);
  const displaySection = item.display_section === "platform" ? "platform" : "manual";

  const myRequests = sortRequestItems((item.my_requests || []).map(mapRequestItem));
  const libraryRequests = sortRequestItems((item.library_requests || []).map(mapRequestItem));

  const riskSummary = item.risk_summary
    ? {
        score: item.risk_summary.score,
        rating: item.risk_summary.rating || "Green",
      }
    : null;

  const lastQualifiedSource = item.last_qualified_request_id
    ? [...myRequests, ...libraryRequests].find(
        (req) => req.id === String(item.last_qualified_request_id)
      )
    : null;

  return ensureVendorGxPProfile({
    id,
    name: item.name || "",
    type: item.provider_type || null,
    primaryLocation,
    membershipSource: displaySection,
    displaySection,
    isManuallyAdded: Boolean(item.is_manually_added),
    isHidden: Boolean(item.is_hidden),
    isEngaged: Boolean(item.is_engaged),
    hasPlatformActivity,
    tags: tags || [],
    serviceCategories,
    services: serviceCategories,
    applicableGxPs: item.applicable_gxps || [],
    demonstratedGxPs: item.demonstrated_gxps || [],
    gxps: item.demonstrated_gxps || [],
    logoUrl: item.logo || null,
    riskSummary,
    lastQualifiedDate: formatQualifiedDate(item.last_qualified_date),
    lastQualifiedRequest:
      item.last_qualified_request_id && item.last_qualified_date
        ? {
            id: String(item.last_qualified_request_id),
            type: lastQualifiedSource?.type || item.last_qualified_request_type || "",
            date: item.last_qualified_date,
            hcQuestionnaireId: lastQualifiedSource?.hcQuestionnaireId ?? null,
            vqaRequestId: lastQualifiedSource?.vqaRequestId ?? null,
          }
        : null,
    myRequests,
    libraryRequests,
    rfiCount: item.rfi_count || 0,
    assessmentCount: item.assessment_count || 0,
    provider,
  });
}

/**
 * Map a Discovery / Providers/Search list item into a My Vendors table row shape
 * so custom-list members missing from the live My Vendors join still show
 * location, docs, type, and GxP.
 */
export function mapProviderSearchItemToVendor(item = {}, options = {}) {
  return mapMyVendorApiItemToVendor(
    {
      ...item,
      id: item.id || item.Id || item.provider_id,
      is_manually_added: false,
      has_platform_activity: false,
      display_section: "manual",
    },
    options
  );
}

export function mapMyVendorApiItemsToVendors(items = [], vendorTagsMap = {}) {
  return items.map((item) =>
    mapMyVendorApiItemToVendor(item, {
      tags: vendorTagsMap[String(item.id)] || [],
    })
  );
}
